import "/node_modules/flag-icons/css/flag-icons.min.css";
import './App.sass';
import { useEffect, useState, type ReactNode } from "react";

import { useNavigate } from "react-router-dom";

import { useAtom } from "jotai";
import { authenticationAtom } from './State';

import { useTranslation } from "react-i18next";

import {
  ArrowLeft,
  ChevronLeft,
  LogOut,
  MessageSquare,
  Settings,
  SidebarOpen,
  Wrench,
} from "lucide-react";

import { userAPI } from "./UserAPI";
import { LanguageMenu } from "@/components/LanguageMenu";
import { invalidatePhaseParcels } from "./portfolio-dashboard/phaseParcelsApi";
import { forgetProjects } from "./cbos/selectedProject";
import { useCustomerLogo } from "./hooks/useCustomerLogo";
import { configuration } from "./Configuration";

import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Properties {
  /** Optionaler Inhalt ganz links (z. B. der SidebarTrigger der Settings-Seite). */
  leading?: ReactNode;
  showSideBar?: boolean;
  showSideBarChanged?: (showSideBar: boolean) => void,
  showBack?: boolean,
  showHome?: boolean,
  showSidePanel?: boolean,
  showMapItem?: boolean,
  showTabs?: boolean,
  showLogo?: boolean,
  toggleSidePanel?: any,
  title?: string,
  selectTab?: any,
  homeClicked?: () => void,
  showSettings?: boolean,
  showAdministration?: boolean,
  /** Kontomenü oben rechts; Settings blendet es aus (Nutzer sitzt dort in der Sidebar). */
  showAccountMenu?: boolean,
}

export enum Tabs {
  Product = 'Product',
  Company = 'Company',
  Security = 'Security',
}

function Header(properties: Properties) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [serverVersion, setServerVersion] = useState<string | null>(null);
  const [authentication, setAuthentication] = useAtom(authenticationAtom) as any;
  const { logo: customerLogo } = useCustomerLogo();

  useEffect(() => {
    fetch(`${configuration.serverUrl}/version`)
      .then((res) => res.json())
      .then((data) => setServerVersion(data.version))
      .catch(() => setServerVersion("unavailable"));
  }, []);

  const logout = () => {
    // Keep the user on their tenant's branded login after logout by carrying
    // the tenant through as ?tenant=<id> (captured before clearing auth).
    const tenant = authentication?.customer;
    setAuthentication(null);
    // Logging out happens inside the page, so anything held in memory outlives it unless it is
    // dropped here. The parcel estimates are tenant data.
    invalidatePhaseParcels();
    forgetProjects();
    userAPI.logout();
    navigate(tenant ? `/login?tenant=${encodeURIComponent(tenant)}` : '/login');
  };

  const back = () => {
    window.history.back()
  }


  const initials = `${(authentication as any)?.firstName?.charAt(0) || ''}${(authentication as any)?.lastName?.charAt(0) || ''}`;

  return (
    <div className="flex min-h-12 w-full max-w-full items-center gap-1 border-b bg-background/90 p-4">
      {properties.leading}
      {properties.showBack && (
        <Button variant="ghost" size="icon" aria-label={t('header.back')} onClick={() => back()}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
      )}
      {properties.showSideBar && (
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('header.toggleSidebar')}
          onClick={() => {
            if (properties.showSideBarChanged) {
              properties.showSideBarChanged(!!properties.showSideBar);
            }
          }}
        >
          <SidebarOpen className="h-4 w-4" />
        </Button>
      )}
      {properties.showHome && (
        <Button
          variant="ghost"
          title={t('header.assistant')}
          aria-label={t('header.assistant')}
          className="gap-1"
          onClick={() => {
            if (properties.homeClicked) {
              properties.homeClicked();
            } else {
              navigate("/chatbot");
            }
          }}
        >
          <ChevronLeft className="h-4 w-4" />
          {t('header.assistant')}
          <MessageSquare className="h-4 w-4" />
        </Button>
      )}
      {properties.showSettings && (
        <Button
          variant="ghost"
          size="icon"
          title={t('header.settings')}
          aria-label={t('header.settings')}
          onClick={() => navigate("/settings")}
        >
          <Settings className="h-4 w-4" />
        </Button>
      )}
      {properties.showAdministration && configuration.shouldSeeAdministration(authentication) && (
        <Button
          variant="ghost"
          size="icon"
          title={t('header.administration')}
          aria-label={t('header.administration')}
          onClick={() => navigate("/administration")}
        >
          <Wrench className="h-4 w-4" />
        </Button>
      )}
      {properties.showLogo && (
        <img
          onClick={() => navigate('/')}
          className="h-[100px] w-[100px] cursor-pointer"
          src={customerLogo}
          alt="logo"
          width={100}
          height={100}
        />
      )}
      <div className="flex min-w-0 flex-1 justify-center">
        <div className="truncate text-lg font-bold [font-family:Montserrat,sans-serif]">
          {properties.title}
        </div>
      </div>
      <LanguageMenu className="mr-2" />
      {(properties.showAccountMenu ?? true) && <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t('header.accountMenu')}
            className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Avatar className="h-10 w-10 max-sm:h-11 max-sm:w-11">
              <AvatarFallback className="bg-secondary text-sm font-medium text-secondary-foreground">
                {initials}
              </AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[12rem]">
          <DropdownMenuItem onClick={() => navigate('/settings')}>
            <Settings className="h-4 w-4" />
            {t('header.settings')}
          </DropdownMenuItem>
          {configuration.shouldSeeAdministration(authentication) && (
            <DropdownMenuItem onClick={() => navigate('/administration')}>
              <Wrench className="h-4 w-4" />
              {t('header.administration')}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout}>
            <LogOut className="h-4 w-4" />
            {t('header.logout')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>{t('header.version')}</DropdownMenuLabel>
          <DropdownMenuItem disabled>{`Portal: ${configuration.version}`}</DropdownMenuItem>
          <DropdownMenuItem disabled>{`Server: ${serverVersion ?? '...'}`}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>}
    </div>
  );
}

export default Header;
